import { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutGrid,
  Building2,
  AlertTriangle,
  SendHorizonal,
  ClipboardList,
  Package,
  BarChart3,
  Users,
  CreditCard,
  Bell,
} from "lucide-react";

type Item = {
  to: string;
  label: string;
  icon: ReactNode;
  ownerOnly?: boolean;
};

type Section = { title?: string; items: Item[] };

const SECTIONS: Section[] = [
  {
    items: [
      { to: "/dashboard", label: "Dashboard", icon: <LayoutGrid className="h-4 w-4" /> },
      { to: "/dashboard/brands", label: "Brands", icon: <Building2 className="h-4 w-4" /> },
      { to: "/dashboard/reviews", label: "Flagged reviews", icon: <AlertTriangle className="h-4 w-4" /> },
      { to: "/dashboard/takedowns", label: "Takedowns", icon: <SendHorizonal className="h-4 w-4" /> },
      { to: "/dashboard/audits", label: "Audits", icon: <ClipboardList className="h-4 w-4" /> },
    ],
  },
  {
    title: "Catalog",
    items: [
      { to: "/dashboard/products", label: "Products", icon: <Package className="h-4 w-4" /> },
      { to: "/dashboard/reports", label: "Reports", icon: <BarChart3 className="h-4 w-4" /> },
    ],
  },
  {
    title: "Settings",
    items: [
      { to: "/dashboard/team", label: "Team", icon: <Users className="h-4 w-4" />, ownerOnly: true },
      { to: "/dashboard/billing", label: "Billing", icon: <CreditCard className="h-4 w-4" />, ownerOnly: true },
      { to: "/dashboard/notifications", label: "Notifications", icon: <Bell className="h-4 w-4" /> },
    ],
  },
];

export function AppSidebar({
  collapsed,
  isOwner,
}: {
  collapsed: boolean;
  isOwner: boolean;
}) {
  return (
    <aside
      className={`sticky top-14 hidden shrink-0 self-start border-r border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 md:block ${
        collapsed ? "w-14" : "w-60"
      }`}
      style={{ height: "calc(100vh - 3.5rem)" }}
    >
      <nav className="flex h-full flex-col overflow-y-auto py-3">
        {SECTIONS.map((section, idx) => {
          const visibleItems = section.items.filter(
            (i) => !i.ownerOnly || isOwner
          );
          if (visibleItems.length === 0) return null;
          return (
            <div key={idx} className={idx > 0 ? "mt-4" : ""}>
              {!collapsed && section.title && (
                <div className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  {section.title}
                </div>
              )}
              <ul className="space-y-0.5 px-2">
                {visibleItems.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === "/dashboard"}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm ${
                          isActive
                            ? "bg-slate-100 font-medium text-slate-900 dark:bg-slate-800 dark:text-slate-100"
                            : "text-slate-700 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                        }`
                      }
                      title={collapsed ? item.label : undefined}
                    >
                      <span className="shrink-0">{item.icon}</span>
                      {!collapsed && <span className="truncate">{item.label}</span>}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
